import * as React from 'react';
import { ApplicationState } from "app/types-store";
import { connect } from 'react-redux';
import { actionCreators as draftActionCreators } from 'app/area/case/draft/actions';
import { useEffect, useRef } from 'react';
import hoistStatics from 'hoist-non-react-statics';

type LatestEtagStateToProps = {
  hasChanges: boolean;
  etag?: string;
};

type LatestEtagDispatchToProps = {
  requestImmediateSave: () => Promise<void>;
};

export const withLatestEtag = (
  Component: React.ComponentType
) => {
  const stateToProps = (appState: ApplicationState): LatestEtagStateToProps => ({
    hasChanges: appState?.caseArea?.editing?.hasChanges,
    etag: appState?.caseArea?.editing?.etag
  });
  const dispatchToProps = (dispatch: any): LatestEtagDispatchToProps => ({
    requestImmediateSave: async () => await dispatch(draftActionCreators.requestImmediateSave())
  });
  const WrapperComponent = (props) => {
    const nextCallbackRef = useRef<() => void | undefined>(undefined);
    const { hasChanges, requestImmediateSave } = props;
    const canProceed = !hasChanges;

    useEffect(() => {
      if (canProceed && !!nextCallbackRef?.current) {
        if (nextCallbackRef.current) {
          const callback = nextCallbackRef.current;
          onContinue(callback);
        }
      }
    }, [canProceed]);

    const onContinue = (callback?: (etag?: string) => void) => {
      const { etag: latestEtag } = props;
      nextCallbackRef.current = undefined;
      callback && callback(latestEtag);
    };

    const waitForLatestEtag = async (callback?: (etag?: string) => void) => {
      if (hasChanges) {
        await requestImmediateSave();
        nextCallbackRef.current = callback;
      } else {
        onContinue(callback);
      }
    };
    const { wrappedComponentRef, ...remainingProps } = props;
    return (
      <Component
        {...remainingProps}
        ref={wrappedComponentRef}
        waitForLatestEtag={waitForLatestEtag}
      />
    );
  };
  return connect(stateToProps, dispatchToProps)(hoistStatics(WrapperComponent, Component));
};
